import React from "react";
import { v4 as uuidv4 } from "uuid";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import {
  usePopupState,
  bindTrigger,
  bindPopover,
} from "material-ui-popup-state/hooks";

import Dot from "../Board/Dot";
import { useTranslation, Trans } from "react-i18next";
import { Box, Grid, Tooltip } from "@mui/material";
import { roundToTwoDecimalPlaces } from "../../tools";

function LabelDot({ id, label, question }) {
  if (id === "") return "";
  return (
    <div style={{ display: "flex", paddingRight: 3, width: "fit-content" }}>
      <Dot label={id} />
      <Tooltip title={question}>
        <Typography variant="subtitle2">{label}:</Typography>
      </Tooltip>
    </div>
  );
}

function Info({ id, analyse, category }) {
  const { t } = useTranslation();
  //console.log(analyse);
  const probability = roundToTwoDecimalPlaces(analyse.pvalue);
  const probabilityInPercent = probability * 100;
  const variance = roundToTwoDecimalPlaces(analyse.Rsquared);
  const varianceInPercent = variance * 100;
  const popupState = usePopupState({
    variant: "popover",
    popupId: id,
  });

  if (analyse.pvalue <= 0.1) {
    const rEffect = () => {
      if (analyse.Rsquared < 0.02)
        return t("moodboards.effect.veryWeak", { category }); // heel zwak
      if (0.02 <= analyse.Rsquared && analyse.Rsquared < 0.13)
        return t("moodboards.effect.weak", { category }); // zwak
      if (0.13 <= analyse.Rsquared && analyse.Rsquared < 0.26)
        return t("moodboards.effect.moderate", { category }); // matig
      if (analyse.Rsquared >= 0.26)
        return t("moodboards.effect.substantial", { category }); // substantieel
    };

    const pConfidence = () => {
      if (0.05 < analyse.pvalue && analyse.pvalue <= 0.1)
        return t("moodboards.confidence.low"); // laag
      if (0.01 < analyse.pvalue && analyse.pvalue <= 0.05)
        return t("moodboards.confidence.medium"); // medium
      if (analyse.pvalue < 0.01) return t("moodboards.confidence.high"); // hoog
    };

    return (
      <div>
        <Box display={"flex"}>
          <Box>
            <LabelDot
              id={analyse.id}
              label={analyse.label}
              question={analyse.question}
            />
          </Box>
          <Box>
            <Typography variant="body2">
              <span
                variant="body2"
                {...bindTrigger(popupState)}
                style={{
                  borderBottom: "2px dotted",
                  cursor: "pointer",
                }}
              >
                {rEffect()}
              </span>{" "}
              ({pConfidence()})
            </Typography>
          </Box>
        </Box>

        {/*     <IconButton size="small" >
            <HelpOutlineOutlinedIcon fontSize="small" />
          </IconButton> */}
        <Popover
          {...bindPopover(popupState)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
        >
          <Typography
            style={{ padding: 16, maxWidth: 330, fontSize: "0.9rem" }}
          >
            <Trans
              i18nKey={"moodboards.effectInfo"}
              probability={probabilityInPercent}
              variance={varianceInPercent}
            >
              There is a <strong>{{ probability: probabilityInPercent }} probability</strong> that
              this effect (or something even more pronounced) can be found by
              chance. The overall F-test of the categorical regression is used
              to compute this probability. This regression can explain{" "}
              <strong>{{ variance: varianceInPercent }}% variance</strong>. The size of this effect
              is computed as the adjusted R².
            </Trans>
          </Typography>
        </Popover>
      </div>
    );
  }
  // analyse.pvalue > 0.1
  return (
    <div style={{ display: "flex" }}>
      <LabelDot
        id={analyse.id}
        label={analyse.label}
        question={analyse.question}
      />
      <Typography variant="body2" key={id}>
        {t("moodboards.effect.none", { category })}
      </Typography>
    </div>
  );
}

export default function RegressionInfo({ analyses, category }) {
  //console.log(analyses)
  return analyses.map((analyse) => (
    <Info
      key={`RegressionInfo${uuidv4()}`}
      analyse={analyse}
      category={category}
    />
  ));
}
